import React from 'react';
import AppText from '../AppText';

const TONE_STYLES = {
	error: {
		role: 'alert',
		surface: 'border-rose-400/25 bg-rose-500/[0.075] text-rose-100 shadow-[0_12px_30px_rgba(244,63,94,0.10)]',
		accent: 'bg-rose-400',
		icon: 'border-rose-300/25 bg-rose-500/15 text-rose-200',
		title: 'text-rose-100',
		message: 'text-rose-100/80',
		button: 'border-rose-300/25 bg-rose-400/10 text-rose-100 hover:border-rose-200/40 hover:bg-rose-400/20',
	},
	warning: {
		role: 'status',
		surface: 'border-amber-400/25 bg-amber-500/[0.075] text-amber-100 shadow-[0_12px_30px_rgba(245,158,11,0.08)]',
		accent: 'bg-amber-300',
		icon: 'border-amber-300/25 bg-amber-500/15 text-amber-200',
		title: 'text-amber-100',
		message: 'text-amber-100/80',
		button: 'border-amber-300/25 bg-amber-400/10 text-amber-100 hover:border-amber-200/40 hover:bg-amber-400/20',
	},
	success: {
		role: 'status',
		surface: 'border-primary/25 bg-primary/[0.075] text-primary shadow-[0_12px_30px_rgba(4,175,118,0.08)]',
		accent: 'bg-primary',
		icon: 'border-primary/25 bg-primary/15 text-primary',
		title: 'text-primary',
		message: 'text-primary/80',
		button: 'border-primary/25 bg-primary/10 text-primary hover:border-primary/40 hover:bg-primary/15',
	},
	info: {
		role: 'status',
		surface: 'border-sky-300/20 bg-sky-500/[0.065] text-sky-100 shadow-[0_12px_30px_rgba(14,165,233,0.08)]',
		accent: 'bg-sky-300',
		icon: 'border-sky-300/20 bg-sky-500/15 text-sky-200',
		title: 'text-sky-100',
		message: 'text-sky-100/75',
		button: 'border-sky-300/20 bg-sky-400/10 text-sky-100 hover:border-sky-200/40 hover:bg-sky-400/20',
	},
};

const TONE_ICONS = {
	error: (
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
	),
	warning: (
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
	),
	success: (
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
	),
	info: (
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M12 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
	),
};

const SIZE_STYLES = {
	sm: {
		wrapper: 'p-2.5',
		iconBox: 'h-8 w-8',
		icon: 'h-4 w-4',
		title: 'text-xs',
		message: 'text-xs',
		gap: 'gap-2.5',
	},
	md: {
		wrapper: 'p-4',
		iconBox: 'h-10 w-10',
		icon: 'h-5 w-5',
		title: 'text-sm',
		message: 'text-sm',
		gap: 'gap-3',
	},
	lg: {
		wrapper: 'p-5',
		iconBox: 'h-12 w-12',
		icon: 'h-6 w-6',
		title: 'text-base',
		message: 'text-sm',
		gap: 'gap-4',
	},
};

const StatusNotice = ({
	tone = 'error',
	title = null,
	message,
	action = null,
	actionLabel = null,
	onAction = null,
	onDismiss = null,
	size = 'md',
	className = '',
	centered = false,
}) => {
	if (!message && !title) return null;

	const toneStyle = TONE_STYLES[tone] || TONE_STYLES.error;
	const sizeStyle = SIZE_STYLES[size] || SIZE_STYLES.md;
	const contentLayout = centered
		? 'flex-col items-center text-center'
		: title
			? 'items-start text-left'
			: 'items-center text-left';

	return (
		<div
			role={toneStyle.role}
			className={`relative overflow-hidden rounded-[var(--qs-radius-panel)] border backdrop-blur-md animate-fadeIn ${toneStyle.surface} ${sizeStyle.wrapper} ${className}`.trim()}
		>
			<div className={`absolute inset-y-0 left-0 w-1 ${toneStyle.accent}`} aria-hidden="true" />
			<div className={`flex ${sizeStyle.gap} ${contentLayout} pl-1`}>
				<div className={`flex shrink-0 items-center justify-center rounded-[var(--qs-radius-icon)] border ${toneStyle.icon} ${sizeStyle.iconBox}`}>
					<svg className={sizeStyle.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
						{TONE_ICONS[tone] || TONE_ICONS.error}
					</svg>
				</div>
				<div className="min-w-0 flex-1">
					{title ? (
						<AppText weight="semibold" className={`${sizeStyle.title} ${toneStyle.title}`}>
							{title}
						</AppText>
					) : null}
					{message ? (
						<AppText className={`${title ? 'mt-1' : ''} ${sizeStyle.message} leading-relaxed ${toneStyle.message}`}>
							{message}
						</AppText>
					) : null}
					{action || actionLabel ? (
						<div className="mt-3">
							{action || (
								<button
									type="button"
									onClick={onAction}
									className={`inline-flex items-center justify-center rounded-[var(--qs-radius-control)] border px-3 py-1.5 text-xs font-semibold transition-colors ${toneStyle.button}`}
								>
									{actionLabel}
								</button>
							)}
						</div>
					) : null}
				</div>
				{onDismiss ? (
					<button
						type="button"
						onClick={onDismiss}
						aria-label="Dismiss notice"
						className="shrink-0 rounded-lg p-1 text-white/45 transition-colors hover:bg-white/10 hover:text-white"
					>
						<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				) : null}
			</div>
		</div>
	);
};

export default StatusNotice;
